import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { SecretariatStore } from '../../store/SecretariatStore';
import { Secretariat } from '../../store/models/Secretariat';
import { RouteComponentProps } from 'react-router-dom';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import SecretariatForm from './SecretariatForm';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import MainSectionLayout from '../layout/MainSectionLayout';
import { Card, Tab, Tabs } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { css } from 'emotion';
import SecretariatPharmaciesTab from './SecretariatPharmaciesTab';
import SecretariatNeighborsTab from './SecretariatNeighborsTab';
import { AuthenticationStore, SystemStatusStore, ApplicationStore, AnchorStore, GroupStore } from '../../store';
import { SecretariatSystemStatus } from './SecretariatSystemStatus';
import { AnchorListPage } from '../Anchor/AnchorListPage';
import { RefreshGeoDataDialog } from '../common/dialog/RefreshGeoDataDialog';
import SecretatiatUsersTab from './SecretatiatUsersTab';
import SecretariatGroupsTab from './SecretariatGroupsTab';

interface SecretariatPageProps extends RouteComponentProps<{ id: string }> {
  SecretariatStore?: Instance<typeof SecretariatStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  GroupStore?: Instance<typeof GroupStore>;
}

interface SecretariatPageState {
  isLoading: boolean;
  secretariat?: Instance<typeof Secretariat>;
  refreshGeoDataDialogDone: boolean;
  refreshGeoDataDialoginProgress: boolean;
  refreshGeoDataDialoginError: boolean;
}

@inject('SecretariatStore', 'AuthenticationStore', 'SystemStatusStore', 'ApplicationStore', 'GroupStore', 'AnchorStore')
@observer
class SecretariatPage extends React.Component<SecretariatPageProps & WithTranslateProps, SecretariatPageState> {
  state = {
    isLoading: false,
    refreshGeoDataDialogDone: false,
    refreshGeoDataDialoginProgress: false,
    refreshGeoDataDialoginError: false,
  } as SecretariatPageState;

  _isMounted = false;

  async fetchData() {
    const { SecretariatStore, SystemStatusStore, AnchorStore, GroupStore } = this.props;
    const secretariatId = parseInt(this.props.match.params.id, 10);
    this._isMounted && this.setState({ isLoading: true });
    await Promise.all([SystemStatusStore!.loadList(), GroupStore!.loadList(), AnchorStore!.loadList()]);
    if (secretariatId > 0) {
      await SecretariatStore!.loadElement(secretariatId);
      const secretariat = SecretariatStore!.getById(secretariatId);
      this._isMounted && this.setState({ secretariat });
    }
    this._isMounted && this.setState({ isLoading: false });
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: SecretariatPageProps, prevState: SecretariatPageState) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.fetchData();
    }
  }

  renderTitle = () => {
    const secretariat = this.state.secretariat;
    return secretariat !== undefined ? (
      <h1>
        {this.props.translate(TLCode.SECRETARIAT_Edit)}&nbsp;
        {secretariat!.name}
      </h1>
    ) : (
      <></> // <h1>{this.props.translate(TLCode.SECRETARIAT_Add)}</h1>
    );
  };

  save = async (data: any) => {
    this._isMounted && this.setState({ isLoading: true });
    await this.props.SecretariatStore!.saveSecretariat(data);
    this._isMounted && this.setState({ isLoading: false });
  };

  canEditSecretariat = () => {
    const { isAdmin, isSuperAdmin, isSecretariat, secretariatId } = this.props.AuthenticationStore!;
    return (
      isAdmin ||
      isSuperAdmin ||
      (this.state.secretariat !== undefined && isSecretariat && secretariatId === this.state.secretariat!.secretariatId)
    );
  };

  refreshSecretariatPharmacyGeodata = (secretariatId: number) => {
    this.setState({ refreshGeoDataDialoginProgress: true });
    const result = (this.props.SecretariatStore!.refreshSecretariatPharmacyGeodata(
      secretariatId,
    ) as unknown) as boolean;
    if (result) {
      this.setState({ refreshGeoDataDialogDone: true, refreshGeoDataDialoginProgress: false });
    } else {
      this.setState({ refreshGeoDataDialoginError: true, refreshGeoDataDialoginProgress: false });
    }
  };

  refreshSecretariatNeighborhood = (secretariatId: number) => {
    this.props.SecretariatStore!.refreshSecretariatNeighborhood(secretariatId);
  };

  private onRefreshGeoDataDialogClosed = () => {
    this.setState({
      refreshGeoDataDialogDone: false,
      refreshGeoDataDialoginProgress: false,
      refreshGeoDataDialoginError: false,
    });
  };

  public render() {
    const {
      isLoading,
      secretariat,
      refreshGeoDataDialogDone,
      refreshGeoDataDialoginError,
      refreshGeoDataDialoginProgress,
    } = this.state;
    const { translate, SystemStatusStore, ApplicationStore } = this.props;
    const params = new URLSearchParams(this.props.location.search);
    const fromPage = params.get('from');
    console.log('fromPage', fromPage);
    
    let defaultTab = 'profile';
    if (fromPage === 'group') {
      defaultTab = 'groups';
    }
    else if (fromPage === 'systemStatusForm') {
      defaultTab = 'systemStatus';
    }
    else if (fromPage === 'anchors') {
      defaultTab = 'anchors';
    }

    const groups = this.props.GroupStore!.dataList.filter(
      (g) => secretariat !== undefined && g.secretariatId === secretariat.secretariatId,
    );
    return (
      <>
        <RefreshGeoDataDialog
          translate={this.props.translate}
          done={refreshGeoDataDialogDone}
          inError={refreshGeoDataDialoginError}
          inProgress={refreshGeoDataDialoginProgress}
          onClose={this.onRefreshGeoDataDialogClosed}
        />
        <MainSectionLayout isLoading={isLoading} renderHeader={this.renderTitle}>
          <Container>
            <Card className={CardStyle.toString()}>
              <DashboardContainer>
                <Tabs defaultSelectedTabId={defaultTab}>
                  <Tab
                    id="profile"
                    title="Profil"
                    panel={
                      <SecretariatForm
                        secretariat={secretariat}
                        save={this.save}
                        readOnly={!this.canEditSecretariat()}
                      />
                    }
                  />
                  <Tab
                    id="pharmacies"
                    title="Pharmacies"
                    panel={
                      <SecretariatPharmaciesTab
                        secretariat={secretariat}
                        readOnly={!this.canEditSecretariat()}
                        refreshSecretariatPharmacyGeodata={this.refreshSecretariatPharmacyGeodata}
                      />
                    }
                  />
                  <Tab
                    id="neighbors"
                    title="Pharmacies limitrophes"
                    panel={<SecretariatNeighborsTab secretariat={secretariat} readOnly={!this.canEditSecretariat()} />}
                  />
                  <Tab
                    id="systemStatus"
                    title={translate(TLCode.SECRETARIAT_SystemStatusTab)}
                    panel={
                      <SecretariatSystemStatus
                        systemStatuses={SystemStatusStore!.dataList}
                        secretariat={secretariat}
                        translate={translate}
                        navigate={ApplicationStore!.navigate}
                      />
                    }
                  />
                  <Tab
                    id="anchors"
                    title={translate(TLCode.SECRETARIAT_Anchors)}
                    panel={
                      <AnchorListPage
                        anchors={this.props.AnchorStore!.dataList.filter((a) =>
                          groups.find((g) => g.groupId === a.groupId),
                        )}
                        groups={groups}
                        deleteAnchor={(id) => this.props.AnchorStore!.deleteElement(id)}
                        goToAnchorForm={(id) => this.props.ApplicationStore!.navigate(`${Page.Anchor}/${id}`)}
                        translate={translate}
                      />
                    }
                  />
                  <Tab
                    id="users"
                    title={translate(TLCode.SECRETARIAT_UsersTab)}
                    panel={
                      secretariat && <SecretatiatUsersTab secretariatId={secretariat.secretariatId} />
                    }
                  />
                  <Tab
                    id="groups"
                    title={translate(TLCode.SECRETARIAT_GroupsTab)}
                    panel={
                      secretariat && <SecretariatGroupsTab secretariatId={secretariat.secretariatId} />
                    }
                  />
                </Tabs>
              </DashboardContainer>
            </Card>
          </Container>
        </MainSectionLayout>
      </>
    );
  }
}

export default withTranslate(SecretariatPage);

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const CardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DashboardContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
  '& .ag-theme-balham': {
    width: '100%',
  },
});
